/**
 * Token-based authentication utilities
 * Handles JWT token exchange for session cookies
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Session ID key for sessionStorage (unique per tab)
const CLIENT_SESSION_ID_KEY = 'mustrust_client_session_id';

// Generate unique session ID for this tab/window (persisted in sessionStorage)
// Using sessionStorage ensures each browser tab has its own unique ID
export function getClientSessionId() {
  let clientSessionId = sessionStorage.getItem(CLIENT_SESSION_ID_KEY);
  
  if (!clientSessionId) {
    clientSessionId = `${Date.now()}_${Math.random().toString(36).substring(7)}`;
    sessionStorage.setItem(CLIENT_SESSION_ID_KEY, clientSessionId);
    console.log('🆔 Generated NEW client session ID:', clientSessionId);
  } else {
    console.log('🆔 Using EXISTING client session ID:', clientSessionId);
  }
  
  return clientSessionId;
}

// Clear client session ID (call when logging out)
export function clearClientSessionId() {
  sessionStorage.removeItem(CLIENT_SESSION_ID_KEY);
  console.log('🗑️ Cleared client session ID');
}

/**
 * Extract token from URL parameter and exchange for session cookie
 * @returns {Promise<boolean>} True if authentication successful
 */
export async function authenticateWithToken() {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  if (!token) {
    return false; // No token present
  }

  try {
    // Exchange token for session cookie
    const response = await fetch(`${BACKEND_URL}/api/auth/wix-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Session-Id': getClientSessionId(),
      },
      credentials: 'include', // Important: include cookies
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('Token authentication failed:', error);
      
      // If concurrent session detected, throw error to prevent fallback
      if (error.error === 'SESSION_ALREADY_ACTIVE') {
        const concurrentError = new Error(error.message || 'Concurrent session detected');
        concurrentError.code = 'SESSION_ALREADY_ACTIVE';
        throw concurrentError;
      }
      
      return false;
    }

    const data = await response.json();

    // Remove token from URL without page reload
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);

    // Return subscription data from token auth
    return {
      authenticated: true,
      subscriptionId: data.subscriptionId,
      email: data.email
    };
  } catch (error) {
    console.error('Token authentication error:', error);
    
    // Re-throw SESSION_ALREADY_ACTIVE errors
    if (error.code === 'SESSION_ALREADY_ACTIVE') {
      throw error;
    }
    
    return false;
  }
}

/**
 * Check if user has valid session
 * @returns {Promise<Object|null>} Session data or null
 * @throws {Error} If concurrent session detected
 */
export async function checkSession() {
  try {
    // Get subscriptionId from URL to ensure we check the correct cookie
    const params = new URLSearchParams(window.location.search);
    const subscriptionId = params.get('subscriptionId');
    
    // Build query string if subscriptionId is present
    const queryParams = new URLSearchParams();
    if (subscriptionId) {
      queryParams.set('subscriptionId', subscriptionId);
    }
    const queryString = queryParams.toString();
    const checkUrl = queryString 
      ? `${BACKEND_URL}/api/session/check?${queryString}`
      : `${BACKEND_URL}/api/session/check`;
    
    const response = await fetch(checkUrl, {
      method: 'GET',
      headers: {
        'X-Client-Session-Id': getClientSessionId(),
      },
      credentials: 'include',
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    
    // Check for concurrent session error
    if (data.error === 'CONCURRENT_SESSION' || (!data.authenticated && data.error)) {
      const concurrentError = new Error(data.message || 'Another session is active for this subscription');
      concurrentError.code = 'SESSION_ALREADY_ACTIVE';
      throw concurrentError;
    }
    
    return data;
  } catch (error) {
    // Re-throw concurrent session errors
    if (error.code === 'SESSION_ALREADY_ACTIVE') {
      throw error;
    }
    console.error('Session check error:', error);
    return null;
  }
}
